import { CONSENT_CATEGORY } from '../../config/types/consent'

import { updateGTMConsent } from './gtm'
import SDKCookieTable from '../generated/SDKCookieTable.json'
import { withDomain } from './configUtils'
import { trackConsent } from './track'
import { TrackConsentQueue } from './trackConsentQueue'
import { Queue } from '../utils/Queue'

export const CONSENT_COOKIE = 'v1_gousto_cookie_consent'

const queue = new Queue(trackConsent)
const trackConsentQueue = new TrackConsentQueue(queue)

export function getConfig(): UserConfig {
  const cookieTableWithDomain = withDomain(SDKCookieTable)

  const GUIOptionsConfig: GUIOptions = {
    consent_modal: {
      layout: 'cloud',
      position: 'bottom right',
      transition: 'slide',
    },
    settings_modal: {
      layout: 'box',
      transition: 'slide',
    },
  }
  const consentModalConfig: ConsentModalLanguageSetting = {
    title: 'We use cookies 🍪',
    primary_btn: {
      text: 'Accept all',
      role: 'accept_all',
    },
    secondary_btn: {
      text: 'Reject all non-essential',
      role: 'accept_necessary',
    },
    description: `The Gousto website uses and stores cookies to sweeten your experience. You can take back or change your consent at any time.
    Check out our  <a href="https://www.gousto.co.uk/privacy-statement" class="cc-link">Cookies Policy</a> for more information or click
    <button class="cc-link" data-cc="c-settings">here</button> to manage your cookie preferences.`,
  }

  const settingsModalConfig: SettingsModalLanguageSetting = {
    title: 'Cookie preferences',
    save_settings_btn: 'Save preferences',
    accept_all_btn: 'Accept all',
    reject_all_btn: 'Reject all',
    close_btn_label: 'Close',
    cookie_table_headers: [{ col1: 'Name' }, { col2: 'Description' }],
    blocks: [
      {
        title: 'Cookie usage 📢',
        description: `
        By clicking "Accept all", you agree that we may store and access cookies on your device to help us understand how you use our website,
        personalise content and improve our online marketing. Don’t worry, you can update your choices for each cookie category whenever you want.<br>
        <br>
        If you click "Reject all non-essential cookies", we’ll continue to use strictly necessary cookies only to make sure our website works properly when signing in,
        storing recipes in your basket, making payments and staying secure.<br>
        <br>
        For more information or to update your settings, check out our
        <a href="https://www.gousto.co.uk/privacy-statement" class="cc-link">Privacy Policy and Cookies Policy</a>.`,
      },
      {
        title: 'Strictly necessary cookies',
        description: `These cookies are essential for the Gousto website to work properly and can’t be switched off.
      They’re used for functions like signing in, storing recipes in your basket, making payments and security.`,
        toggle: {
          value: CONSENT_CATEGORY.STRICTLY_NECESSARY,
          enabled: true,
          readonly: true,
        },
        cookie_table:
          cookieTableWithDomain[CONSENT_CATEGORY.STRICTLY_NECESSARY] || [],
      },
      {
        title: 'Functionality cookies',
        description:
          'These cookies aren’t essential for the Gousto website to work properly and can be switched off. They’re used for helpful features such as remembering your settings and preferences.',
        toggle: {
          value: CONSENT_CATEGORY.FUNCTIONALITY,
          enabled: false,
          readonly: false,
        },
        cookie_table:
          cookieTableWithDomain[CONSENT_CATEGORY.FUNCTIONALITY] || [],
      },
      {
        title: 'Analytics cookies',
        description:
          'We’d like to collect website analytics information occasionally using trusted third parties to help us improve our website. The data is collected in a way that does not directly identify anyone.',
        toggle: {
          value: CONSENT_CATEGORY.ANALYTICS,
          enabled: false,
          readonly: false,
        },
        cookie_table: cookieTableWithDomain[CONSENT_CATEGORY.ANALYTICS] || [],
      },
      {
        title: 'Marketing cookies',
        description: `
        We’d like to use marketing cookies which are usually placed by trusted third parties with our permission to save data on websites and products you’ve looked at.
        We might use these cookies to advertise Gousto when you visit other websites.
        Check out our <a href="https://www.gousto.co.uk/privacy-statement" class="cc-link">Privacy Policy and Cookies Policy</a> for more information on how we might advertise to you.`,
        toggle: {
          value: CONSENT_CATEGORY.MARKETING,
          enabled: false,
          readonly: false,
        },
        cookie_table: cookieTableWithDomain[CONSENT_CATEGORY.MARKETING] || [],
      },
    ],
  }

  return {
    autorun: true,
    autoclear_cookies: true,
    cookie_name: CONSENT_COOKIE,
    cookie_expiration: 182,
    current_lang: 'en',
    force_consent: true,
    hide_from_bots: true,
    page_scripts: true,
    remove_cookie_tables: true,
    gui_options: GUIOptionsConfig,
    languages: {
      en: {
        consent_modal: consentModalConfig,
        settings_modal: settingsModalConfig,
      },
    },
    onFirstAction(_, storedConsent) {
      updateGTMConsent()
      trackConsentQueue.onFirstAction(storedConsent)
    },
    onAccept(storedConsent) {
      updateGTMConsent()
      trackConsentQueue.onAccept(storedConsent)
    },
    onChange(storedConsent) {
      updateGTMConsent()
      trackConsentQueue.onChange(storedConsent)
    },
    revision: 1,
  }
}
